// Translator notes: Phase 3 items 16–17, phase 4 item 17: borrowed names, referents, letters and the register
// (engine/src/names.ts, discourse.ts, letter-parts.ts, tone.ts).
// Each id is an engine note (`notes.add(text, id, args)`); `args` names its arguments in the order the
// strings take them. Collected by translator/notes.ts (import.meta.glob): adding a note means adding it here.
import { defineNotes } from '../note-defs';
import { GENDER_FI, GENDER_ADJ_FI, GENDER_EN } from './words';

export const notes = defineNotes({
  borrowedName: {
    args: ['word', 'hv', 'declension', 'gender', 'model', 'changes'],
    fi: (a: string[]) =>
      `${a[0]} ei ole sanakirjassa, joten se taivutetaan lainanimenä: ${a[1]}${a[5] ? ` (kirjoitusasu ${a[5]})` : ''}, ${a[2]}. deklinaatio, ${GENDER_FI[a[3]!] ?? a[3]}, kuten ${a[4]} (oppitunnit 20, 29 ja 41).`,
    en: (a: string[]) =>
      `${a[0]} is not in the dictionary, so it is inflected as a borrowed name: ${a[1]}${a[5] ? ` (spelling ${a[5]})` : ''}, declension ${a[2]}, ${GENDER_EN[a[3]!] ?? a[3]}, like ${a[4]} (lessons 20, 29 and 41).`,
  },
  referentGender: {
    args: ['word', 'hv', 'gender'],
    fi: (a: string[]) => `”${a[0]}” viittaa ${GENDER_ADJ_FI[a[2]!] ?? a[2]} sanaan, joten valyriaksi se on ${a[1]} (oppitunnit 11 ja 18).`,
    en: (a: string[]) => `“${a[0]}” refers to a ${GENDER_EN[a[2]!] ?? a[2]} noun, so in Valyrian it is ${a[1]} (lessons 11 and 18).`,
  },
  formalWish: {
    args: ['title'],
    fi: (a: string[]) =>
      `Muodollinen sävy: pyyntö on toivomus, jossa arvonimi ${a[0]} on vokatiivissa ja verbi infinitiivissä (oppitunti 42). Rakenne on oppitunnin, mutta juuri tätä lausetta ei ole Petersonin teksteissä.`,
    en: (a: string[]) =>
      `Formal tone: the request is a wish, with the title ${a[0]} in the vocative and the verb in the infinitive (lesson 42). The construction is the lesson's, but this exact sentence is not in Peterson's texts.`,
  },
  letterOpener: {
    args: ['hv'],
    fi: (a: string[]) =>
      `Kirjeen puhuttelu ${a[0]}: nimi vokatiivissa ja sen jäljessä jorrāeliarza ’rakas’, kuten Talisan kirjeessä ”Muñus jorrāeliarzus:” (oppitunti 41).`,
    en: (a: string[]) =>
      `Letter opening ${a[0]}: the name in the vocative followed by jorrāeliarza ‘dear’, as in Talisa’s letter “Muñus jorrāeliarzus:” (lesson 41).`,
  },
  letterOpenerFormal: {
    args: ['hv', 'title'],
    fi: (a: string[]) => `Muodollinen puhuttelu ${a[0]}: nimi vokatiivissa ja arvonimi ${a[1]}, kuten hovissa puhutellaan kuningatarta (oppitunti 41).`,
    en: (a: string[]) => `Formal address ${a[0]}: the name in the vocative and the title ${a[1]}, as the court addresses the queen (lesson 41).`,
  },
  letterGreeting: {
    args: ['hv'],
    fi: (a: string[]) => `Kirjeen alku ${a[0]}: tervehdys ja nimi vokatiivissa (oppitunti 41).`,
    en: (a: string[]) => `Letter opening ${a[0]}: a greeting and the name in the vocative (lesson 41).`,
  },
  letterCloser: {
    args: ['hv', 'formula'],
    fi: (a: string[]) =>
      `Lopetukselle ”${a[1]}” ei ole valyriankielistä vastinetta, joten käytettiin valitun sävyn lopetusta ${a[0]} (oppitunti 41). Kirjeen voi lopettaa myös pelkkään nimeen.`,
    en: (a: string[]) =>
      `The closing “${a[1]}” has no Valyrian wording, so the closing ${a[0]} of the chosen tone was used (lesson 41). A letter may also end with just the name.`,
  },
  letterSignature: {
    args: ['hv'],
    fi: (a: string[]) => `Viimeinen rivi on allekirjoitus: ${a[0]}, nimi perusmuodossa (oppitunti 41).`,
    en: (a: string[]) => `The last line is the signature: ${a[0]}, the name in the nominative (lesson 41).`,
  },
  letterMode: {
    args: [],
    fi: () => 'Kirje käännettiin rivi riviltä, ja kappaleet säilyivät. ”Se”, ”hän” ja ”sen” seuraavat edellisillä riveillä mainittua.',
    en: () => 'The letter was translated line by line, keeping its paragraphs. “It”, “he/she” and “its” follow what the lines before named.',
  },
});

declare module '../note-defs' {
  interface NoteIds extends Record<keyof typeof notes, true> {}
}
